import { useEffect } from "react";
import { type FallbackProps } from "react-error-boundary";
import { Button } from "@/components/atoms/button";
import { lowerFirst } from "lodash-es";

const MESSAGE_SUFFIX =
  "Something went wrong.\nIf you see this message again, please contact the administrator.";

const normalizeGivenNameForCopy = (
  givenName: string | undefined,
): string | undefined => {
  if (typeof givenName !== "string") return undefined;
  const trimmed = givenName.trim();
  return trimmed !== "" ? trimmed : undefined;
};

const genericErrorMessageParagraph = (
  displayName: string | undefined,
): string => {
  if (displayName != null) {
    return `Sorry ${displayName}, ${lowerFirst(MESSAGE_SUFFIX)}`;
  }
  return `${MESSAGE_SUFFIX}`;
};

export type AppErrorBoundaryLayoutProps = {
  resetErrorBoundary: FallbackProps["resetErrorBoundary"];
  givenName?: string;
  onLogout?: () => void;
  error?: unknown;
};

export const AppErrorBoundaryLayout = ({
  resetErrorBoundary,
  givenName,
  onLogout,
  error,
}: AppErrorBoundaryLayoutProps) => {
  const handlePrimaryClick = () => {
    resetErrorBoundary();
    window.location.reload();
  };

  const displayName = normalizeGivenNameForCopy(givenName);

  useEffect(() => {
    console.error("ErrorBoundary caught an error:", error);
  }, [error]);

  return (
    <div
      className="
        flex min-h-screen flex-col items-center justify-center gap-6 p-8
        font-sans
      "
    >
      <h1 className="text-center text-2xl font-semibold">Oops!</h1>
      <p className="max-w-md text-center text-lg font-semibold">
        {genericErrorMessageParagraph(displayName)}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button type="button" onClick={handlePrimaryClick}>
          Refresh
        </Button>
        {onLogout != null ? (
          <Button type="button" variant="outline" onClick={onLogout}>
            Log out
          </Button>
        ) : null}
      </div>
    </div>
  );
};
